import React from 'react';
import {
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { addBusinessDays } from 'date-fns';
import { PriorityOptions } from '../../../types/workflow';
import type { CreateBatchFormData } from './validation';

interface WorkflowSettingsSectionProps {
  /** React Hook Form control object */
  control: Control<CreateBatchFormData>;
  /** Form errors */
  errors: FieldErrors<CreateBatchFormData>;
  /** Whether the form is currently submitting */
  disabled?: boolean;
}

/**
 * WorkflowSettingsSection Component
 *
 * Renders the workflow settings inputs:
 * - Priority dropdown (Urgent, High, Normal, Low)
 * SLA due date is auto-calculated (5 business days) on creation and editable afterward.
 */
export const WorkflowSettingsSection: React.FC<WorkflowSettingsSectionProps> = ({
  control,
  errors,
  disabled = false,
}) => {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" color="primary" gutterBottom>
        Workflow Settings
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        SLA due date defaults to 5 business days from creation.
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="priority"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.priority} disabled={disabled}>
                <InputLabel id="priority-label">Priority</InputLabel>
                <Select
                  {...field}
                  labelId="priority-label"
                  label="Priority"
                >
                  {PriorityOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="slaExpectedDate"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="SLA Due Date"
                  format="dd/MM/yyyy"
                  value={field.value || addBusinessDays(new Date(), 5)}
                  onChange={(date) => field.onChange(date)}
                  disabled={disabled}
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: 'Defaults to 5 business days',
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>

      </Grid>
    </Paper>
  );
};

export default WorkflowSettingsSection;
